/**
 * Shadow Metadata Hooks
 * Hooks for fetching and processing shadow metadata
 */

import { useAtom } from 'jotai';
import { atomWithQuery } from 'jotai-tanstack-query';
import { useMemo } from 'react';
import { knowledgeBaseService } from '@/services/knowledgeBase.service';
import { ruleService } from '@/services/rule.service';

/**
 * Helper to create stable string key from array of IDs
 */
function getStableIdKey(ids: string[]): string {
  return ids.length === 0 ? '' : [...ids].sort().join(',');
}

/**
 * Query atom for fetching multiple knowledge bases by IDs
 */
const knowledgeBasesByIdsQueryAtom = (idKey: string) =>
  atomWithQuery(() => {
    const ids = idKey ? idKey.split(',') : [];
    return {
      queryKey: ['knowledge-bases', 'by-ids', idKey],
      queryFn: () => knowledgeBaseService.getKnowledgeBasesByIds(ids),
      enabled: ids.length > 0
    };
  });

/**
 * Query atom for fetching multiple rules by IDs
 */
const rulesByIdsQueryAtom = (idKey: string) =>
  atomWithQuery(() => {
    const ids = idKey ? idKey.split(',') : [];
    return {
      queryKey: ['rules', 'by-ids', idKey],
      queryFn: () => ruleService.getRulesByIds(ids),
      enabled: ids.length > 0
    };
  });

/**
 * Hook to fetch knowledge bases by IDs
 */
export function useKnowledgeBasesByIds(ids: string[] = []) {
  // Calculate stable key - this is cheap and ensures stable comparison
  const idKey = getStableIdKey(ids);
  const atom = useMemo(() => knowledgeBasesByIdsQueryAtom(idKey), [idKey]);
  const [query] = useAtom(atom);

  if (ids.length === 0) {
    return {
      data: [],
      isLoading: false,
      isError: false,
      isIdle: true
    } as const;
  }

  return query;
}

/**
 * Hook to fetch rules by IDs
 */
export function useRulesByIds(ids: string[] = []) {
  // Calculate stable key - this is cheap and ensures stable comparison
  const idKey = getStableIdKey(ids);
  const atom = useMemo(() => rulesByIdsQueryAtom(idKey), [idKey]);
  const [query] = useAtom(atom);

  if (ids.length === 0) {
    return {
      data: [],
      isLoading: false,
      isError: false,
      isIdle: true
    } as const;
  }

  return query;
}

